import { IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import LayersIcon from '@material-ui/icons/Layers'
import MapIcon from '@material-ui/icons/MapOutlined'
import SatelliteIcon from '@material-ui/icons/Satellite'
import StraightenIcon from '@mui/icons-material/Straighten'
import { Stack } from '@mui/material'
import { ParentSize } from '@visx/responsive'
import classNames from 'classnames'
import { round } from 'lodash'
import lodashMap from 'lodash/map'
import upperFirst from 'lodash/upperFirst'
import mapboxgl, { FullscreenControl } from 'mapbox-gl'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import {
  AgencySettings_EnabledFeaturesEnum,
  TripOriginDestination,
  TripsAggregations,
  TripCountInStreetSegment,
  TripOriginDestination_KindEnum,
  Stops_LineTypeEnum,
  Favorite_PageIdEnum,
} from 'src/api/gen'
import { ITripListFilters } from 'src/api/types/trips'
import useComponentControl from 'src/commons/hooks/useComponentControl'
import useDeepCompareEffect from 'src/commons/hooks/useDeepCompareEffect'
import useSafeState from 'src/commons/hooks/useSafeState'
import { useURLAndStorageState } from 'src/commons/hooks/useURLAndStorageState'
import { DrawLayer, MapActionBar, MapBoxMap, MapPopup, MapProgressBar, MapSpinner } from 'src/commons/map'
import BikeStationPopup from 'src/commons/map/BikeStationPopup'
import { useHandleGBFSData } from 'src/commons/map/hooks/useHandleGBFSData'
import LayersPopup, { DockedStationEnum } from 'src/commons/map/LayersPopup'
import StopPopup from 'src/commons/map/StopPopup'
import { fetchAndUpdateStops, IClickedStopPoint } from 'src/commons/map/stops'
import { ISelectedStation } from 'src/commons/map/types'
import { addImagesToMap } from 'src/commons/map/utils'
import WarnMessage from 'src/components/WarnMessage'
import {
  useAgencySettingsDistanceUnit,
  useAgencySettingsSpeedUnit,
  useCheckEnabledFeature,
} from 'src/layout/AgencySettings'
import { useCityBoundaries, useProviders } from 'src/layout/hooks'
import { FilteringCard } from 'src/muiv5/components/commons/Cards/FilteringCard'
import MeasurementPanel from 'src/pages/SupervisionMap/MeasurementPanel'
import theme, { colors } from 'src/theme'

import { getInitialFilters, INITIAL_FACETS } from '../common'
import { Heatmap, SECONDS_IN_MINUTE } from '../constants'
import FilterPanel from '../FilterPanel'
import {
  fetchFacetedTripList,
  fetchOriginDestinationTripCount,
  fetchTigerTripAggregations,
  fetchTripCountInStreetSegment,
} from '../requests'

import LegendRadioButtons from './Legend/LegendRadioButtons'
import QuantileBasedLegend, { createQuantileBasedColorScale, IQuantileColorScale } from './Legend/QuantileBasedLegend'
import { addLayersToMap, displayOnlyLayerCorrespondingToHeatmap, setupStreetSegmentPopup } from './TripHeatmap.map'

const STREET_SEGMENTS_PALETTE = [
  '#84CA50', // light green
  '#F07D02', // orange
  '#E60000', // light red
  '#9E1313', // dark red
]
const STREET_SEGMENTS_LEGEND_QUANTILES = [50, 75, 95]

// If you want to change the palette, create a new gradient with https://online-free-tools.com/fr/css_color_hex_degrade
const ORIGIN_DESTINATION_PALETTE = ['#91B8DF', '#9CA5C3', '#A792A7', '#B27F8B', '#BD6C6F', '#C95953', '#D44637']
const ORIGIN_DESTINATION_QUANTILES = [50, 75, 90, 95, 98, 99.5]

const INITIAL_TRIP_COUNTS_IN_STREET_SEGMENTS = {
  street_segments: {},
  documents: [],
  trips_per_street_min: 0,
  trips_per_street_max: 0,
  total_page_num: 1,
}

function TripHeatmap(props: { apiOptions?: Record<string, unknown>; disabledOriginDestination?: boolean }) {
  const classes = useStyles()
  const { t } = useTranslation()

  const cityBoundaries = useCityBoundaries()
  const { activeProviders } = useProviders()
  const { value: speedUnit } = useAgencySettingsSpeedUnit()
  const { value: distanceUnit } = useAgencySettingsDistanceUnit()

  const defaultFilters = useMemo(
    () => getInitialFilters(cityBoundaries, speedUnit, distanceUnit),
    [cityBoundaries, speedUnit, distanceUnit]
  )
  const [filters, setFilters] = useURLAndStorageState(Favorite_PageIdEnum.trips_heatmap, defaultFilters, undefined, {
    dateStartRange: defaultFilters.dateStartRange,
    dateEndRange: defaultFilters.dateEndRange,
  })
  const displayedFiltersRef = useRef<ITripListFilters>()

  const [measure, setMeasure] = useSafeState(false)
  const [tripsFacets, setTripsFacets] = useSafeState<TripsAggregations>(INITIAL_FACETS)

  const onChangeMapLayer = useCallback(
    (key: string) => {
      setFilters({ ...filters, origins: [], destinations: [], layer: key })
    },
    [filters]
  )

  // Check enabled features
  let isOriginDestinationHeatmapEnabled = useCheckEnabledFeature()(
    AgencySettings_EnabledFeaturesEnum.heatmap_origin_destination
  )
  if (props.disabledOriginDestination) {
    isOriginDestinationHeatmapEnabled = false
  }
  const isTripsHeatmapEnabled = useCheckEnabledFeature()(AgencySettings_EnabledFeaturesEnum.heatmap_trips)
  const [map, setMap] = useSafeState<mapboxgl.Map | undefined>(undefined)
  const [isMapInitialized, setIsMapInitialized] = useSafeState<boolean>(false)
  const [selectedHeatmap, setSelectedHeatmap] = useSafeState<Heatmap>(
    isOriginDestinationHeatmapEnabled ? Heatmap.Origin : Heatmap.Trips
  )
  const [tripsCount, setTripsCount] = useSafeState<number>(0)

  const [tripsWithOriginsDestinations, setTripsWithOriginsDestinations] = useSafeState<TripOriginDestination[]>([])
  const [tripOriginColorScale, setTripOriginColorScale] = useSafeState<IQuantileColorScale | null>(null)
  const [tripDestinationColorScale, setTripDestinationColorScale] = useSafeState<IQuantileColorScale | null>(null)

  const [tripCountsInStreetSegments, setTripCountsInStreetSegments] = useSafeState<TripCountInStreetSegment>(
    INITIAL_TRIP_COUNTS_IN_STREET_SEGMENTS
  )
  const [tripStreetSegmentColorScale, setTripStreetSegmentColorScale] = useSafeState<IQuantileColorScale | null>(null)

  const [tripHeatmapsDataProgress, setTripHeatmapsDataProgress] = useSafeState<number>(0)
  const [isLoadingOriginDest, setIsLoadingOriginDest] = useSafeState<boolean>(false)
  const [warnMessage, setWarnMessage] = useState<string | null>(null)

  const [useSatellite, setUseSatellite] = useSafeState(false)
  const [selectedStation, setSelectedStation] = useSafeState<ISelectedStation | undefined>(undefined)
  const [mousePointer, setMousePointer] = useSafeState(false)
  const [clickedStopPoint, setClickedStopPoint] = useSafeState<IClickedStopPoint | undefined>(undefined)
  const [areStopsLoading, setAreStopsLoading] = useSafeState(true)
  const [displayStopsPopup, setDisplayStopsPopup] = useState<boolean>(false)

  function onMouseEnter() {
    setMousePointer(true)
  }

  function onMouseLeave() {
    setMousePointer(false)
  }

  const { toggleGBFSLayers } = useHandleGBFSData({
    api,
    map,
    onMouseEnter,
    onMouseLeave,
    setSelectedStation,
  })

  useEffect(() => {
    if (map) {
      addImagesToMap(map, activeProviders)
      map.addControl(new FullscreenControl(), 'bottom-right')
      setupStreetSegmentPopup(map)
    }
  }, [map, activeProviders])

  useEffect(() => {
    if (!map) {
      return
    }

    setWarnMessage(null)
    if (tripCountsInStreetSegments.error_message?.length) {
      setWarnMessage(`${t('tripCountsInStreetSegments.error.street_segments_overload')}`)
    }

    let tripOriginColorScale = null
    let tripDestinationColorScale = null
    if (tripsWithOriginsDestinations.length) {
      tripOriginColorScale = createQuantileBasedColorScale(
        tripsWithOriginsDestinations
          .filter((trip) => trip.kind === TripOriginDestination_KindEnum.origin)
          .map((trip) => trip.trip_count),
        ORIGIN_DESTINATION_PALETTE,
        ORIGIN_DESTINATION_QUANTILES
      )
      tripDestinationColorScale = createQuantileBasedColorScale(
        tripsWithOriginsDestinations
          .filter((trip) => trip.kind === TripOriginDestination_KindEnum.destination)
          .map((trip) => trip.trip_count),
        ORIGIN_DESTINATION_PALETTE,
        ORIGIN_DESTINATION_QUANTILES
      )
    }
    setTripOriginColorScale(tripOriginColorScale)
    setTripDestinationColorScale(tripDestinationColorScale)

    let tripStreetSegmentColorScale = null
    if (tripCountsInStreetSegments.documents.length) {
      // warning message if street_segments > 100 000
      console.info(`loaded ${Object.keys(tripCountsInStreetSegments.street_segments).length} street_segments`)
      tripStreetSegmentColorScale = createQuantileBasedColorScale(
        lodashMap(tripCountsInStreetSegments.documents, 'doc_count'),
        STREET_SEGMENTS_PALETTE,
        STREET_SEGMENTS_LEGEND_QUANTILES
      )
    }
    setTripStreetSegmentColorScale(tripStreetSegmentColorScale)

    addLayersToMap(
      map,
      () => setIsMapInitialized(true),
      tripsWithOriginsDestinations,
      tripCountsInStreetSegments,
      tripOriginColorScale,
      tripDestinationColorScale,
      tripStreetSegmentColorScale
    )
    displayOnlyLayerCorrespondingToHeatmap(map, selectedHeatmap)
  }, [map, tripsWithOriginsDestinations, tripCountsInStreetSegments])

  const fetchDataForTripHeatmaps = async function (signal: AbortSignal) {
    try {
      setTripHeatmapsDataProgress(0)
      setIsLoadingOriginDest(true)

      async function fetchAndSetTripsWithOriginsDestinations() {
        const tripOriginDestinationCount = await fetchOriginDestinationTripCount(filters, signal, props.apiOptions)
        if (!signal.aborted) {
          setTripsWithOriginsDestinations(tripOriginDestinationCount)
          setIsLoadingOriginDest(false)
        }
      }
      async function fetchAndSetTripCountsInStreetSegments() {
        if (isTripsHeatmapEnabled) {
          const allTripCountInStreetSegmentsResult: TripCountInStreetSegment = {
            ...INITIAL_TRIP_COUNTS_IN_STREET_SEGMENTS,
          }
          let next_page: number | undefined = 1
          while (next_page != undefined) {
            const tripCountInStreetSegmentsResult: TripCountInStreetSegment = await fetchTripCountInStreetSegment(
              filters,
              next_page,
              signal,
              props.apiOptions
            )
            if (signal.aborted) {
              return
            }
            allTripCountInStreetSegmentsResult.trips_per_street_max = Math.max(
              allTripCountInStreetSegmentsResult.trips_per_street_max,
              tripCountInStreetSegmentsResult.trips_per_street_max
            )
            allTripCountInStreetSegmentsResult.trips_per_street_min = Math.min(
              allTripCountInStreetSegmentsResult.trips_per_street_min,
              tripCountInStreetSegmentsResult.trips_per_street_min
            )
            allTripCountInStreetSegmentsResult.street_segments = {
              ...allTripCountInStreetSegmentsResult.street_segments,
              ...tripCountInStreetSegmentsResult.street_segments,
            }
            allTripCountInStreetSegmentsResult.documents = [
              ...allTripCountInStreetSegmentsResult.documents,
              ...tripCountInStreetSegmentsResult.documents,
            ]
            setTripHeatmapsDataProgress(next_page / tripCountInStreetSegmentsResult.total_page_num)
            next_page = tripCountInStreetSegmentsResult.next_page
          }
          if (!signal.aborted) {
            setTripCountsInStreetSegments(allTripCountInStreetSegmentsResult)
            setTripHeatmapsDataProgress(1)
          }
        } else {
          setTripCountsInStreetSegments(INITIAL_TRIP_COUNTS_IN_STREET_SEGMENTS)
        }
      }
      const asyncCalls: [Promise<void>, Promise<void>] = [
        fetchAndSetTripsWithOriginsDestinations(),
        fetchAndSetTripCountsInStreetSegments(),
      ]
      await Promise.all(asyncCalls)
    } catch (error) {
      if (!signal.aborted) {
        console.error('ERROR while fetching trips heatmaps data', error)
      }
    } finally {
      setTripHeatmapsDataProgress(1)
    }
  }

  const fetchDataForTripList = async function (signal?: AbortSignal) {
    try {
      if ((props.apiOptions?.query as { backend?: string } | undefined)?.backend === 'tiger') {
        const aggResponse = await fetchTigerTripAggregations(filters, signal)
        if (signal?.aborted) {
          return
        }
        const { count, aggregations: facets } = aggResponse
        if (!count) {
          setTripsCount(0)
          setTripsFacets(INITIAL_FACETS)
          return
        }
        let { duration_min, duration_max } = facets
        duration_min = round((duration_min || 0) / SECONDS_IN_MINUTE)
        duration_max = round((duration_max || 0) / SECONDS_IN_MINUTE)
        setTripsCount(count)
        setTripsFacets({ ...facets, duration_min, duration_max })
      } else {
        // TODO(2023-03-30): pass the signal to fetchFacetedTripList (which would require changes in TripsTable)
        const facetedListResponse = await fetchFacetedTripList(filters, 0, { includeDocuments: false })
        if (signal?.aborted) {
          return
        }
        if (!facetedListResponse.number_of_matching_documents) {
          setTripsCount(0)
          setTripsFacets(INITIAL_FACETS)
          return
        }
        let { duration_min, duration_max } = facetedListResponse.aggregations
        duration_min = round((duration_min || 0) / SECONDS_IN_MINUTE)
        duration_max = round((duration_max || 0) / SECONDS_IN_MINUTE)
        setTripsCount(facetedListResponse.number_of_matching_documents)
        setTripsFacets({ ...facetedListResponse.aggregations, duration_min, duration_max })
      }
    } catch (error) {
      console.error('ERROR while fetching trips list and filters data', error)
    }
  }

  const fetchStops = async function (signal?: AbortSignal) {
    await fetchAndUpdateStops(
      api,
      filters.stopsLayers,
      map,
      setClickedStopPoint,
      setAreStopsLoading,
      () => setIsMapInitialized(true),
      signal
    )
  }

  useDeepCompareEffect(() => {
    if (!map) {
      return
    }

    const abortController = new AbortController()
    setClickedStopPoint(undefined)
    fetchDataForTripList(abortController.signal)
    fetchDataForTripHeatmaps(abortController.signal)
    fetchStops(abortController.signal)
    return () => abortController.abort()
  }, [filters, map, props.apiOptions])

  function onToggleCheck(heatmap: Heatmap) {
    setSelectedHeatmap(heatmap)
    if (map) {
      displayOnlyLayerCorrespondingToHeatmap(map, heatmap)
    }
  }

  const onApplyFilters = function () {
    if (!displayedFiltersRef.current) {
      throw new Error('There are no displayed filters')
    }

    setFilters((pendingFilters) => ({ ...pendingFilters, ...displayedFiltersRef.current }))
  }

  function toggleLayer(label: Stops_LineTypeEnum) {
    const newValue = filters.stopsLayers.includes(label)
      ? filters.stopsLayers.filter((layer) => layer !== label)
      : [...filters.stopsLayers, label]
    setFilters({ ...filters, stopsLayers: newValue })
  }

  function toggleDockedStationLayer(label: DockedStationEnum) {
    if (!map) {
      return
    }

    const newValue = filters.dockedStationLayers.includes(label)
      ? filters.dockedStationLayers.filter((layer) => layer !== label)
      : [...filters.dockedStationLayers, label]

    setFilters({ ...filters, dockedStationLayers: newValue })
    toggleGBFSLayers(newValue)
  }

  const componentControl = useComponentControl(
    <>
      <MapActionBar>
        <IconButton onClick={() => setDisplayStopsPopup(!displayStopsPopup)}>
          <LayersIcon />
        </IconButton>
        <IconButton onClick={() => setUseSatellite(!useSatellite)}>
          {useSatellite ? <MapIcon /> : <SatelliteIcon />}
        </IconButton>
      </MapActionBar>
      <MapActionBar fitContent>
        <IconButton onClick={() => setMeasure(!measure)}>
          <StraightenIcon color={measure ? 'primary' : 'inherit'} />
        </IconButton>
      </MapActionBar>
    </>,
    [useSatellite, displayStopsPopup, measure]
  )

  return (
    <>
      <FilteringCard
        SideComponent={
          <FilterPanel
            ref={displayedFiltersRef}
            initialFilters={filters}
            defaultFilters={defaultFilters}
            providers={activeProviders}
            tripsCount={tripsCount}
            tripsFacets={tripsFacets}
            onChangeMapLayer={onChangeMapLayer}
            tripCountsInStreetSegments={tripCountsInStreetSegments}
            isHeatmapTripsSelected={selectedHeatmap === Heatmap.Trips}
            apiOptions={props.apiOptions}
          />
        }
        onApplyFilter={onApplyFilters}
      >
        <ParentSize
          parentSizeStyles={{
            borderTopRightRadius: 'inherit',
            borderBottomRightRadius: 'inherit',
            width: '100%',
            height: '100%',
          }}
          className={classNames({ forceMousePointer: mousePointer })}
        >
          {({ height }) => (
            <Stack flexDirection="column">
              {warnMessage && <WarnMessage message={warnMessage} />}
              <MapBoxMap
                height={height}
                options={{
                  logoPosition: 'top-right',
                }}
                onMapReady={async (map) => {
                  setMap(map)
                  await toggleGBFSLayers(filters.dockedStationLayers, map)
                }}
                showSatellite={useSatellite}
                showScale="bottom-right"
                showSearchBar="top-left"
                componentControl={componentControl}
              >
                <div className={classes.legendContainer}>
                  {isOriginDestinationHeatmapEnabled && (
                    // if origin destination is not enabled, there is only one choice so we don't need show it
                    <>
                      <LegendRadioButtons selectedHeatmap={selectedHeatmap} onToggleCheck={onToggleCheck} />
                      <div className={classes.separator} />
                    </>
                  )}
                  <div className={classes.legend}>
                    {isOriginDestinationHeatmapEnabled && selectedHeatmap === Heatmap.Origin && (
                      <QuantileBasedLegend
                        noDataText={upperFirst(t('trips.heatmap.trips_heatmap.no_legend'))}
                        quantilesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.quantiles_text'))}
                        valuesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.origin_values_text'))}
                        colorScale={tripOriginColorScale}
                      />
                    )}
                    {isOriginDestinationHeatmapEnabled && selectedHeatmap === Heatmap.Destination && (
                      <QuantileBasedLegend
                        noDataText={upperFirst(t('trips.heatmap.trips_heatmap.no_legend'))}
                        quantilesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.quantiles_text'))}
                        valuesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.destination_values_text'))}
                        colorScale={tripDestinationColorScale}
                      />
                    )}
                    {isTripsHeatmapEnabled && selectedHeatmap === Heatmap.Trips && (
                      <QuantileBasedLegend
                        noDataText={upperFirst(t('trips.heatmap.trips_heatmap.no_legend'))}
                        quantilesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.quantiles_text'))}
                        valuesText={upperFirst(t('trips.heatmap.trips_heatmap.legend.street_segments_values_text'))}
                        colorScale={tripStreetSegmentColorScale}
                      />
                    )}
                  </div>
                </div>
                {selectedStation && selectedStation.station && (
                  <MapPopup coordinates={selectedStation.coordinates} verticalOffset={-20}>
                    <BikeStationPopup provider={selectedStation.provider} station={selectedStation.station} />
                  </MapPopup>
                )}
                {clickedStopPoint && clickedStopPoint.stop && (
                  <MapPopup coordinates={clickedStopPoint.coordinates} verticalOffset={-20}>
                    <StopPopup stop={clickedStopPoint.stop} provider={clickedStopPoint.provider} />
                  </MapPopup>
                )}
                {displayStopsPopup && (
                  <LayersPopup
                    toggleLayer={toggleLayer}
                    activeStopsLayers={filters.stopsLayers}
                    toggleDockedStationLayer={toggleDockedStationLayer}
                    activeDockedStationLayers={filters.dockedStationLayers}
                  />
                )}
                <DrawLayer active={measure} mode="draw_line_string">
                  <MeasurementPanel onCancel={() => setMeasure(false)} />
                </DrawLayer>
              </MapBoxMap>
              {selectedHeatmap === Heatmap.Trips ? (
                <MapProgressBar progress={isMapInitialized ? tripHeatmapsDataProgress : 0} />
              ) : (
                <MapSpinner loading={!isMapInitialized || isLoadingOriginDest || areStopsLoading} />
              )}
            </Stack>
          )}
        </ParentSize>
      </FilteringCard>
    </>
  )
}

const useStyles = makeStyles(() => ({
  root: {
    flex: 1,
    display: 'flex',
    borderRadius: 'inherit',
  },
  card: {
    minHeight: 820,
    height: '100%',
    borderTopRightRadius: 'inherit',
    borderBottomRightRadius: 'inherit',
  },
  centerCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  exportIcon: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  heatmapWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    borderTopRightRadius: 'inherit',
    borderBottomRightRadius: 'inherit',
  },
  legendContainer: {
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    height: 120,
    padding: 24,
    position: 'absolute',
    bottom: 20,
    left: 20,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 2px 0 rgba(0, 0, 0, 0.18)',
  },
  legend: {
    width: 340,
  },
  separator: {
    margin: '0 24px',
    minWidth: 2,
    height: 30,
    backgroundColor: colors.grey,
  },
}))

export default TripHeatmap
